// De taaklade, met de markup van het prototype (DRAWER in design/build/prototype.js).
// Direct opgeslagen, met Ongedaan maken: afwerken, prioriteit, eigenaars, checklist, labels, afhankelijkheden, zelf
// oppakken. Bij Opslaan: een nieuwe start en deadline uit de kalender (of een voorstel voor de deadline) en de notities.
// Rechten: de planner wijzigt alles; een medewerker alleen eigen taken, en een taak zonder eigenaar pakt die alleen
// voor zichzelf op. Schrijven vanuit de app kan enkel met een Project-licentie (me.canWrite); zonder licentie is de lade
// alleen-lezen en opent een knop de taak in Planner zelf. Een nieuwe deadline voorstellen kan wel: dat blijft in de app.
// De secties (eigenaars, labels, wacht op, checklist, bijlagen) en de rechten staan in components/task/ en worden
// gedeeld met de taakpagina van het scherm Taken; "Volledig bewerken" in de kop opent die pagina.
// Drie tabbladen, met enkel hun naam: Taak (alles hierboven), Uren (Clockify: meter, timer, de Clockify-taak en gelogd
// per dag, TaskHours; los van de Project-licentie) en Verloop (statusupdates en wat er met de taak gebeurde, TaskHistory).
import { useQueryClient } from "@tanstack/react-query";
import { Fragment, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { api } from "../lib/api";
import { catOf, labelColor } from "../lib/board";
import { faseChain } from "../lib/fases";
import { useIndex, useTaskOps } from "../lib/hooks";
import { Icon } from "../lib/icons";
import { ddmm, deadline, dmy, PRIORITIES, priorityLabel } from "../lib/planning";
import type { PlanningData, Task, TaskOp } from "../lib/types";
import { useToast } from "../shell/Toast";
import { DateField } from "./DateField";
import { TaskHistory } from "./TaskHistory";
import { TaskHours } from "./TaskHours";
import { AttachmentsSection } from "./task/Attachments";
import { ChecklistSection } from "./task/Checklist";
import { DepsSection } from "./task/Deps";
import { LabelsSection } from "./task/Labels";
import { OwnersSection } from "./task/Owners";
import { firstNames, useTaskPerm } from "./task/perm";

export function TaskDrawer({ task, data, onClose }: { task: Task; data: PlanningData; onClose: () => void }) {
  const perm = useTaskPerm(task);
  const idx = useIndex(data);
  const ops = useTaskOps();
  const toast = useToast();
  const qc = useQueryClient();
  const [, setParams] = useSearchParams();
  // een nieuwe start en deadline uit de kalender; ze gaan mee bij Opslaan
  const [newStart, setNewStart] = useState<string | null>(null);
  const [newEnd, setNewEnd] = useState<string | null>(null);
  const [notes0] = useState(task.notes);
  const [notes, setNotes] = useState(task.notes);
  const [tab, setTab] = useState<"taak" | "uren" | "verloop">("taak");
  if (!perm) return null;

  const { me, med, own, canEdit, canPick, readOnly, write, edit, inPlanner, finishOther, direct, proposeEnd, proposal, url } = perm;
  const proposing = !!newEnd && !direct;
  const start = newStart || task.start, end = newEnd || task.end;
  const cat = catOf(task), d = deadline(task.end, data.today);
  const klant = idx.klanten.get(task.projectId)?.name || "";
  const bucket = idx.buckets.get(task.bucketId)?.name || "";
  // het pad van fases boven de taak; elke fase opent de faselade (in de schil, bij ?fase=)
  const chain = faseChain(task.faseId, idx.fases);
  const openFase = (id: string) => setParams((p) => { p.delete("taak"); p.set("fase", id); return p; });
  const ownerNames = task.owners.map((o) => idx.people.get(o.personId)?.first || "iemand");
  const run = (o: TaskOp[], message: string) => { ops.run(task, o, message).catch(() => {}); };

  // een start na de deadline neemt de deadline mee, een deadline voor de start de start (zoals Planner het wil)
  const pickStart = (s: string) => { setNewStart(s === task.start ? null : s); if (s > end) setNewEnd(s === task.end ? null : s); };
  const pickEnd = (e: string) => { setNewEnd(e === task.end ? null : e); if (direct && e < start) setNewStart(e === task.start ? null : e); };

  const save = async () => {
    const o: TaskOp[] = [];
    if (direct && (newStart || newEnd)) o.push({ kind: "dates", ...(newStart ? { start: newStart } : {}), ...(newEnd ? { end: newEnd } : {}) });
    if (notes !== notes0) o.push({ kind: "notes", text: notes });
    try {
      if (proposing && newEnd) {
        await api.propose(task.id, newEnd);
        qc.invalidateQueries({ queryKey: ["proposals"] });
        toast.flash("Voorstel " + ddmm(newEnd) + " verstuurd naar de planner");
      }
      // meteen dicht: de wijziging staat al op het bord; mislukt ze, dan zegt een melding dat
      if (o.length) ops.run(task, o, "Opgeslagen · zichtbaar in Planner binnen 1 à 2 minuten").catch(() => {});
      onClose();
    } catch { /* de melding toont de fout */ }
  };

  return (
    <>
      <button type="button" className="p-scrim" aria-label="Detail sluiten" onClick={onClose} />
      <aside className="p-drawer" role="dialog" aria-modal="true" aria-labelledby="dt">
        <div className="p-drawer-h">
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
            <div style={{ minWidth: 0 }}>
              <p className="ex-eyebrow" style={{ margin: 0 }}>{klant} · {bucket}</p>
              {chain.length > 0 && (
                <div className="p-dw-pad">
                  <Icon name="fase" size={13} />
                  {chain.map((f, i) => <Fragment key={f.id}>{i > 0 && <span>›</span>}<button type="button" className="p-dw-padl" title="Fase openen" onClick={() => openFase(f.id)}>{f.title}</button></Fragment>)}
                </div>
              )}
              <h2 id="dt">{task.title}</h2>
            </div>
            <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
              <Link className="ex-btn" to={"/taken?taak=" + task.id} title="Alles van deze taak op één scherm, in Taken"><Icon name="checklist" size={15} />Volledig bewerken</Link>
              <a className="ex-btn" href={url} target="_blank" rel="noreferrer" title="Deze taak openen in Planner"><Icon name="ext" size={15} />Planner</a>
              <button type="button" className="ex-icon-btn ex-icon-btn--square" aria-label="Sluiten" title="Sluiten" onClick={onClose}><Icon name="x" size={18} /></button>
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 10 }}>
            <span className={"ex-pill ex-pill--" + (cat === "actief" ? d.tone || "off" : "off")}><span className="ex-dot" />{cat === "actief" ? d.long : cat === "geparkeerd" ? "Geparkeerd" : "Klaar om af te sluiten"}</span>
            {task.labels.map((l) => <span key={l.id} className="p-lbl"><i style={{ background: labelColor(l.name) }} />{l.name}</span>)}
            {task.critical && <span className="p-crit">Kritisch pad</span>}
            {task.pending && <span className="p-meta-i t-mute">Planner verwerkt een wijziging</span>}
          </div>
          <div className="p-dtabs" role="tablist" aria-label="Taak, uren of verloop">
            <button type="button" role="tab" className="p-dtab" aria-selected={tab === "taak"} onClick={() => setTab("taak")}>Taak</button>
            <button type="button" role="tab" className="p-dtab" aria-selected={tab === "uren"} onClick={() => setTab("uren")}>Uren</button>
            <button type="button" role="tab" className="p-dtab" aria-selected={tab === "verloop"} onClick={() => setTab("verloop")}>Verloop</button>
          </div>
        </div>

        {tab === "verloop" ? (
          <div className="p-drawer-b p-vl">
            <TaskHistory task={task} data={data} canLabel={edit} write={write} owners={firstNames(ownerNames)} plannerUrl={url} />
          </div>
        ) : tab === "uren" ? (
          <div className="p-drawer-b p-ur">
            <TaskHours task={task} me={me} klant={klant} today={data.today} manage={!med || own} planner={!med} owners={firstNames(ownerNames)}
              firstOf={(pid) => idx.people.get(pid)?.first || "iemand"} />
          </div>
        ) : <div className="p-drawer-b" style={{ overflowY: "auto" }}>
          {readOnly && (
            <div className="ex-notice ex-notice--mist p-notice"><Icon name="info" size={16} />
              <p>{task.owners.length ? "Taak van " + firstNames(ownerNames) + ". U kunt ze bekijken; wijzigen en afwerken doet " + firstNames(ownerNames) + " of de planner."
                : write ? "Deze taak heeft nog geen eigenaar. U kunt ze zelf oppakken; aan een collega toewijzen doet de planner."
                : "Deze taak heeft nog geen eigenaar. Oppakken doet u in Planner: wijs de taak daar aan uzelf toe. Binnen een minuut staat ze in uw kolom."}</p>
            </div>
          )}
          {inPlanner && canEdit && (
            <div className="ex-notice ex-notice--mist p-notice"><Icon name="info" size={16} />
              <p>Wijzigen en afwerken doet u in Planner. Wat u daar aanpast, staat binnen een minuut ook hier.{med ? " Een nieuwe deadline stelt u hier voor aan de planner." : ""}</p>
            </div>
          )}
          {proposal && (
            <div className="ex-notice ex-notice--info p-notice"><Icon name="info" size={16} />
              <p>{proposal.by === me.id ? "Uw voorstel" : "Een voorstel"} voor een nieuwe deadline, {dmy(proposal.end)}, wacht op de planner.</p>
            </div>
          )}

          <div className="p-fs"><h3>Planning</h3>
            <div className="p-grid2">
              <DateField label="Start (vanaf wanneer)" value={start} was={newStart && task.start} today={data.today} other={end} otherLabel="Deadline"
                onPick={direct ? pickStart : undefined} onReset={() => pickStart(task.start)} />
              <DateField label={proposing ? "Deadline (uw voorstel)" : "Deadline"} value={end} was={newEnd && task.end} today={data.today} other={start} otherLabel="Start" align="right"
                onPick={direct || proposeEnd ? pickEnd : undefined} onReset={() => pickEnd(task.end)} />
            </div>
            <div className="p-hrow"><span className="ex-label">Prioriteit</span>
              {edit
                ? <div className="p-quick">{PRIORITIES.map(([p, label]) => <button key={p} type="button" className={"p-tog p-tog-s" + (task.priority === p ? " on" : "")} onClick={() => run([{ kind: "priority", priority: p }], "Prioriteit " + label.toLowerCase() + ": " + task.title)}>{label}</button>)}</div>
                : <span className={"p-prio p" + task.priority}>{priorityLabel(task.priority)}</span>}
            </div>
            <OwnersSection task={task} data={data} perm={perm} run={run} />
          </div>

          {edit && <div className="p-fs"><h3>Labels</h3><LabelsSection task={task} data={data} perm={perm} run={run} /></div>}

          <div className="p-fs"><DepsSection task={task} data={data} perm={perm} ops={ops} /></div>

          {(task.checklist.length > 0 || edit) && <div className="p-fs"><ChecklistSection task={task} perm={perm} run={run} /></div>}

          <div className="p-fs"><h3>Notities</h3>
            {edit
              ? <><textarea className="ex-input p-notes" rows={3} placeholder="Afspraken, contactpersoon, wat een collega moet weten" aria-label="Notities" value={notes} onChange={(e) => setNotes(e.target.value)} /><p className="p-kd-note">Gaat mee naar Planner bij Opslaan.</p></>
              : task.notes ? <p className="p-notes-ro">{task.notes}</p> : <p className="p-kd-note" style={{ margin: 0 }}>Geen notities.</p>}
          </div>

          <div className="p-fs"><AttachmentsSection task={task} perm={perm} run={run} /></div>
        </div>}

        <div className="p-drawer-f">
          {(edit || finishOther) && <button type="button" className="ex-btn" style={{ color: "var(--t-ok)", borderColor: "var(--ok-bd)", background: "var(--ok-bg)" }} onClick={() => { run([{ kind: "progress", progress: 1 }], "Afgewerkt: " + task.title); onClose(); }}><Icon name="check" size={15} />Afwerken</button>}
          {!edit && <span className="t-mute" style={{ fontSize: 13 }}>{canPick ? (write ? "Wordt uw taak, in Planner" : "Oppakken doet u in Planner") : inPlanner ? (proposing ? "De planner keurt de nieuwe datum goed" : "Wijzigen doet u in Planner") : "Alleen bekijken"}</span>}
          <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {edit && <>
              <span className="t-mute" style={{ fontSize: 13 }}>{proposing ? "De planner keurt de nieuwe datum goed" : "Verschijnt binnen 1 à 2 minuten in Planner"}</span>
              <button type="button" className="ex-btn" onClick={onClose}>Annuleren</button>
              <button type="button" className="ex-btn ex-btn--primary" onClick={save}>{proposing ? "Voorstel versturen" : "Opslaan"}</button>
            </>}
            {!edit && <button type="button" className="ex-btn" onClick={onClose}>Sluiten</button>}
            {!edit && proposing && <button type="button" className="ex-btn ex-btn--primary" onClick={save}>Voorstel versturen</button>}
            {inPlanner && !proposing && <a className="ex-btn ex-btn--primary" href={url} target="_blank" rel="noreferrer"><Icon name="ext" size={15} />{canPick ? "Oppakken in Planner" : "Openen in Planner"}</a>}
            {canPick && write && <button type="button" className="ex-btn ex-btn--primary" onClick={() => run([{ kind: "owner", personId: me.id, on: true }], "U bent nu eigenaar van " + task.title)}>Zelf oppakken</button>}
          </span>
        </div>
      </aside>
    </>
  );
}
